import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function QuizPage() {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);

  const handleSubmit = React.useCallback(async () => {
    const formatted = Object.entries(answers).map(([questionId, answer]) => ({
      questionId: parseInt(questionId),
      answer
    }));

    try {
      const res = await axios.post(`/attempts/${quizId}/submit`, { answers: formatted });
      setResult(res.data.data);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
  }, [answers, quizId]);

  // ดึง quiz data
  useEffect(() => {
    axios.get(`/quizzes/${quizId}`).then(res => {
      setQuiz(res.data.data);
      if (res.data.data.timeLimit) setTimeLeft(res.data.data.timeLimit * 60);
      setLoading(false);
    });
  }, [quizId]);

  // Timer
  useEffect(() => {
    if (timeLeft > 0 && !submitted) {
      const timer = setInterval(() => setTimeLeft(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (timeLeft === 0 && !submitted && quiz?.timeLimit) {
      handleSubmit();
    }
  }, [timeLeft, submitted, quiz, handleSubmit]);

  const handleAnswer = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) return <div className="text-center py-12">กำลังโหลด...</div>;
  if (!quiz) return <div className="text-center py-12">ไม่พบแบบทดสอบ</div>;
  if (submitted && result) return <QuizResult result={result} />;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm p-6 border mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{quiz.title}</h1>
            <p className="text-gray-500">{quiz.description}</p>
          </div>
          {quiz.timeLimit > 0 && (
            <div className={`text-xl font-mono font-bold px-4 py-2 rounded-lg ${
              timeLeft < 60 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
            }`}>
              {formatTime(timeLeft)}
            </div>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {quiz.questions.map((q, idx) => (
          <div key={q.id} className="bg-white rounded-xl shadow-sm p-6 border">
            <div className="flex items-start gap-3">
              <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                {idx + 1}
              </span>
              <div className="flex-grow">
                <p className="font-medium mb-4">{q.question}</p>
                
                {q.type === 'multiple_choice' && q.options && (
                  <div className="space-y-2">
                    {q.options.map((opt, optIdx) => (
                      <label
                        key={optIdx}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          answers[q.id] === String(optIdx) ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`question-${q.id}`}
                          value={optIdx}
                          checked={answers[q.id] === String(optIdx)}
                          onChange={() => handleAnswer(q.id, String(optIdx))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                )}
                
                {q.type === 'true_false' && (
                  <div className="flex gap-4">
                    {['True', 'False'].map(val => (
                      <button
                        key={val}
                        onClick={() => handleAnswer(q.id, val.toLowerCase())}
                        className={`flex-1 py-3 rounded-lg border transition ${
                          answers[q.id] === val.toLowerCase() ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-50'
                        }`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                )}
                
                {q.type === 'essay' && (
                  <textarea
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={4}
                    placeholder="เขียนคำตอบ..."
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswer(q.id, e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition"
      >
        ส่งคำตอบ
      </button>
    </div>
  );
}

function QuizResult({ result }) {
  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className={`rounded-xl p-8 text-center ${result.passed ? 'bg-green-50' : 'bg-red-50'}`}>
        <div className={`text-6xl font-bold mb-4 ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
          {result.percentage}%
        </div>
        <p className={`text-xl mb-2 ${result.passed ? 'text-green-700' : 'text-red-700'}`}>
          {result.passed ? 'ผ่าน! 🎉' : 'ไม่ผ่าน'}
        </p>
        <p className="text-gray-600">
          คะแนน {result.totalScore} / {result.maxScore}
        </p>
      </div>
    </div>
  );
}
